import * as React from "react";
import { useState } from "react";

import * as _ from "lodash";

import { colorsPalettes } from "@similarweb/styles";
import { SearchInput } from "@similarweb/ui-components";

import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    ButtonContainer,
    StyledTableTitle,
    TableTopContainer,
} from "app/pages/website-analysis/subdomains/StyledComponents";
import { subdomainsTableColumnsConfigGen } from "app/pages/website-analysis/subdomains/SubdomainsTableColumnConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TableWrapper } from "../../conversion/conversionTableContainer/StyledComponents";

interface ISubdomainsTableProps {
    tableData: any;
    isLoading?: boolean;
    tableExcelUrl?: string;
    isCompare?: boolean;
}

export const SubdomainsTable = (props: ISubdomainsTableProps) => {
    const [searchString, setSearchString] = useState("");
    const [sortedColumn, setSortedColumn] = useState<{
        field: string;
        sortDirection: "desc" | "asc";
    }>({
        field: "Share",
        sortDirection: "desc",
    });

    const isEmptyData = !(props.tableData?.TotalCount > 0);

    const onSearch = React.useCallback((val: string) => {
        setSearchString(val.trim().toLowerCase());
    }, []);

    const onSort = React.useCallback(({ field, sortDirection }) => {
        setSortedColumn({
            field,
            sortDirection,
        });
    }, []);

    const { isLoading, tableExcelUrl, tableData, isCompare } = props;

    const filteredData = React.useMemo(() => {
        let filteredData = [];
        if (!isLoading && tableData) {
            const { Data } = tableData;
            filteredData =
                !isLoading && searchString !== ""
                    ? _.filter(Data, (dataItem: any) => {
                          return dataItem.Domain.toLowerCase().indexOf(searchString) !== -1;
                      })
                    : Data;
        }
        return filteredData;
    }, [isLoading, tableData, searchString]);

    const sortedFilteredData = React.useMemo(() => {
        function orderByWithNullsAtEnd(collection: any[], field: string, sortDirection: "desc" | "asc") {
            const partition = _.partition(collection, (x) => !!_.get(x, field, null));

            return _.concat(
                _.orderBy(partition[0], field === "Domain" ? "Domain" : field, sortDirection),
                partition[1],
            );
        }

        return orderByWithNullsAtEnd(filteredData, sortedColumn.field, sortedColumn.sortDirection);
    }, [filteredData, sortedColumn.field, sortedColumn.sortDirection]);

    const tableColumns = React.useMemo(() => {
        const { tableData } = props;
        if (!tableData) {
            return [];
        }

        const columns = subdomainsTableColumnsConfigGen(
            sortedColumn.field,
            sortedColumn.sortDirection,
            props.isCompare,
        );

        const totalCount = props.tableData?.TotalCount;
        const domainDisplayName = columns[1]?.displayName;
        if (totalCount && domainDisplayName && domainDisplayName === "Domain") {
            return columns.map((col, idx) => {
                if (idx === 1) {
                    return {
                        ...col,
                        displayName: domainDisplayName + ` (${totalCount})`,
                    };
                }
                return col;
            });
        }
        return columns;
    }, [props.isCompare, sortedColumn.field, sortedColumn.sortDirection, props.tableData]);

    const sortedFilteredTableData = React.useMemo(() => {
        return { Data: sortedFilteredData, TotalCount: sortedFilteredData?.length };
    }, [sortedFilteredData, sortedFilteredData]);

    const tableTitle: string | undefined = proNavigator.current().viewConfig?.title;

    return (
        <TableWrapper loading={isLoading}>
            {!isEmptyData ? (
                <>
                    {tableTitle ? (
                        <StyledTableTitle
                            fontVariant="h5"
                            color={colorsPalettes.carbon[500]}
                        >
                            {i18nFilter()(tableTitle)}
                        </StyledTableTitle>
                    ) : null}
                    <TableTopContainer isCompare={isCompare}>
                        <SearchInput
                            defaultValue={searchString}
                            debounce={100}
                            onChange={onSearch}
                            placeholder={i18nFilter()("website.analysis.subdomains.table.search.placeholder")}
                            disableClear={true}
                        />
                        {tableExcelUrl ? (
                            <ButtonContainer>
                                <ExcelButton
                                    url={tableExcelUrl}
                                    trackName="website analysis subdomains"
                                    className={"excel-button-wrapper"}
                                />
                            </ButtonContainer>
                        ) : null}
                    </TableTopContainer>
                </>
            ) : null}
            <SWReactTable
                onSort={onSort}
                tableColumns={tableColumns}
                isLoading={isLoading}
                tableData={sortedFilteredTableData}
            />
        </TableWrapper>
    );
};
